import { apiClient, getData, getPage, type Page } from '@/api/client'
import type { components, operations } from '@/api/schema'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * The query string an endpoint accepts, taken from the generated schema rather
 * than restated.
 *
 * Restating it is how `MoversParams` came to be missing `sort`, `order`, `page`
 * and `page_size` for a day after mm-be added them: the generated types had the
 * params, the hand-written interface did not, and nothing failed to compile.
 */
type Query<K extends keyof operations> = NonNullable<operations[K]['parameters']['query']>

/** `today` is the latest session; the rest sum the last 5 / 22 / 66 trading days. */
export type BrokersPeriod = NonNullable<Query<'api_v5_market_brokers_list'>['period']>

export type MarketRange = '1M' | '1W' | '1Y' | '3M' | 'ALL'

export const MOVERS_MAX_LIMIT = 100

/**
 * `/market/search/`'s own ceiling — `limit` is `max_value=50`
 * (mm-be api/v5/market/serializers.py `MarketSearchRequestSerializer`, and
 * `MAX_LIMIT = 50` in services/market/search.py).
 *
 * Named because the generated types cannot carry it: OpenAPI gives `limit?:
 * number` with no bound, so `search(q, 200)` compiled, went out, and came back
 * a 400 that failed the whole fan-out it sat in. Two callers were doing exactly
 * that — Stock prices and the Companies list — which is why searching either
 * screen errored instead of filtering it.
 */
export const SEARCH_MAX_LIMIT = 50

/**
 * `/market/live/`'s own ceiling — `limit` is `max_value=500`
 * (`MAX_LIMIT = 500` in mm-be services/market/live.py, "500 > the traded
 * universe"). It is a LIMIT and not a page: the endpoint takes no `page` and
 * merges no `PageMeta`, so it cannot be walked. The ceiling now sits above the
 * ~250 companies that trade, so one call at it returns the whole market and
 * `rows.length` is the real total. Under the old ceiling of 200 the 52 least
 * traded names were silently missing from Live market and Stock prices.
 *
 * Should the market ever outgrow 500, a row count equal to the limit is what a
 * clip would look like.
 */
export const LIVE_MAX_LIMIT = 500

/**
 * `/market/fifty-two-week/`'s own ceiling — `limit` is `maximum: 500` in the v5
 * spec, and 1000 is a 400 VALIDATION_ERROR. Raised from 200, which is what made
 * the 52-week page say "the 200 companies nearest their bound".
 *
 * Still a LIMIT and not a page (no `page`, and `meta` is only `count`), but 500
 * sits above the ~240 companies the endpoint ranks, so one call at the ceiling
 * returns every one of them and `rows.length` is the real total. Should the
 * market ever outgrow 500, a `meta.count` equal to the limit is what a clip
 * would look like.
 */
export const FIFTY_TWO_WEEK_MAX_LIMIT = 500

/**
 * The widest page `/market/movers/` will serve — `page_size` is `max_value=100`
 * (mm-be api/v5/market/serializers.py `MoversRequestSerializer`), tighter than
 * the shared pager's 200 (services/common/paging.py `MAX_PAGE_SIZE`), so the
 * serializer is the binding one and 200 here would be a 400.
 *
 * Unlike `/market/live/`'s `limit`, this is a PAGE: the view merges `PageMeta`
 * and mm-be counts the matching set before slicing it
 * (services/market/repository.py `get_movers`), so `meta.total` is a fact about
 * the market and a caller can walk to the end of the list.
 */
export const MOVERS_MAX_PAGE_SIZE = 100

/**
 * `/market/updates/`'s own ceiling — `page_size` is `max_value=50`
 * (mm-be api/v5/market/serializers.py `MarketUpdatesRequestSerializer`; the
 * service restates it as `MAX_PAGE_SIZE = 50` in services/market/updates.py).
 * Lower than the shared 200, and asking for more is a 400.
 */
export const UPDATES_MAX_PAGE_SIZE = 50

/**
 * The only resolution `/charts/history/` serves.
 *
 * mm-be `services/chart/resolutions.py` accepts `1D` or `D` and answers
 * anything else with a 400 `RESOLUTION_UNSUPPORTED` ("Only daily bars are
 * available"). Intraday is deferred there until the `live_stock_price` ticks
 * table is adopted and its freshness validated, so there is no minute bar to
 * ask for and no `1D` chip to draw — see `OverviewIndexPanel`'s RANGES.
 */
export const CHART_RESOLUTION = '1D'

/**
 * Whether the bars come back adjusted for bonus and right issues.
 *
 * ── Why `true`, which is also mm-be's default ────────────────────────────────
 *
 * Two independent reasons, either of which would settle it.
 *
 * 1. NEPSE companies issue bonus shares constantly. On the ex-date an
 *    UNADJUSTED series drops by the ratio of the issue — a 1:1 bonus halves
 *    the printed price overnight — and a candlestick chart draws that as a
 *    catastrophic gap down that never happened. `adjusted=true` reads
 *    `open/high/low/close`, which mm-be has already rebased, so a bar from two
 *    years ago is comparable with today's.
 *
 * 2. It is the only setting under which the candles AGREE with the line beside
 *    them. `/market/indices/{key}/history/` serves `IndexPoint.value` from
 *    `stock_price.ltp` (mm-be `services/market/indices.py` `bar_to_point`) —
 *    the adjusted column family. `adjusted=false` would read the as-traded
 *    `u_open/u_high/u_low/u_close/u_ltp` instead, so toggling Line→Candles
 *    would move every historical price and the member would reasonably
 *    conclude one of the two charts is lying.
 *
 * As-traded is the right basis for a different question — "what did I actually
 * pay on that day" — which belongs to the portfolio, not to a price chart.
 * Sent explicitly rather than relying on mm-be's default so that the decision
 * is visible at the call site rather than inherited from a server default that
 * could change.
 */
export const CHART_ADJUSTED = 'true'

/**
 * The most bars `/charts/history/` will serve in one windowed request —
 * `MAX_BARS` in mm-be `services/chart/resolutions.py`, restated as
 * `max_value=MAX_BARS` on `HistoryRequestSerializer.countback`, so a larger
 * `countback` is a 400 and not a truncation.
 *
 * Named because the generated types cannot carry it, for the same reason
 * `SEARCH_MAX_LIMIT` above is named. A request with NO window at all is capped
 * higher still (`FULL_HISTORY_MAX_BARS`, 20,000) and that is how `ALL` is
 * asked for — see `barWindow.ts`.
 */
export const CHART_MAX_COUNTBACK = 5000

export type EventsParams = Query<'api_v5_market_events_list'>
export type EventType = NonNullable<EventsParams['type']>
export type SectorSort = 'change' | 'turnover' | 'az'

/** What sizes the heatmap's tiles, in mm-be's words: `market_cap`, `turnover`, `volume`. */
export type HeatmapSizing = NonNullable<Query<'api_v5_market_heatmap_list'>['sizing']>

export type MoversParams = Query<'api_v5_market_movers_list'>
export type MoverType = MoversParams['type']
/** The column names the ⇅ headers may sort on — mm-be's names, not the table's. */
export type MoversSort = NonNullable<MoversParams['sort']>

/**
 * Derived, not restated: this interface was hand-written and missed the `order`
 * mm-be added (`recent` | `amount` | `amount_asc`), the one that makes "Largest
 * trades" a server read instead of a client sort of a single page.
 */
export type FloorsheetParams = Query<'api_v5_market_floorsheet_list'>

export interface UpdatesParams {
  page?: number
  page_size?: number
  limit?: number
  category?: string
  q?: string
}

export const marketApi = {
  /**
   * Daily OHLCV bars for one stock or index — the TradingView UDF datafeed's
   * `getBars`, asked for by BAR COUNT, which is what lets a 200-day average be
   * one request. `/market/stocks/{symbol}/price-history/` carries a session per
   * date too (prices, % change, volume, turnover) but takes a calendar range.
   * An index's sessions also ride on its own `/market/indices/{key}/history/`
   * points, which is where the index charts read them.
   *
   * `countback` is "the N most recent drawable bars", and it takes priority
   * over `from` in mm-be (`services/chart/bars.py`), which is why no `from` is
   * sent with it. Omitting `countback` entirely asks for the whole series —
   * see `barWindow.ts` for why a count and not a date window.
   */
  chartHistory: (symbol: string, countback?: number): Promise<Wire<'BarSeries'>> =>
    getData(() =>
      apiClient.GET('/api/v5/charts/history/', {
        params: {
          query: {
            symbol,
            resolution: CHART_RESOLUTION,
            adjusted: CHART_ADJUSTED,
            ...(countback === undefined ? {} : { countback }),
          },
        },
      }),
    ),

  /**
   * `period` is omitted for the latest session, which is the server's default, so a caller that
   * means "today" shares one cache entry (`endpointCache` keys on the arguments) with the callers
   * that never name a period.
   */
  brokers: (limit = 50, period?: BrokersPeriod): Promise<Page<Wire<'BrokerActivity'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/market/brokers/', {
        params: { query: { limit, ...(period === undefined ? {} : { period }) } },
      }),
    ),

  depth: (symbol: string): Promise<Wire<'DepthBook'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/depth/{symbol}/', {
        params: { path: { symbol } },
      }),
    ),

  events: (query: EventsParams): Promise<Page<Wire<'MarketEvent'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/events/', { params: { query } })),

  fiftyTwoWeek: (
    basis: 'high' | 'low',
    limit = FIFTY_TWO_WEEK_MAX_LIMIT,
  ): Promise<Page<Wire<'FiftyTwoWeekRow'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/market/fifty-two-week/', {
        params: { query: { basis, limit } },
      }),
    ),

  floorsheet: (query: FloorsheetParams): Promise<Page<Wire<'FloorsheetTrade'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/floorsheet/', { params: { query } })),

  floorsheetSummary: (date?: string): Promise<Wire<'FloorsheetSummary'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/floorsheet/summary/', {
        params: { query: date === undefined ? {} : { date } },
      }),
    ),

  /**
   * Two levels on one URL. With no `sector` the rows are one tile per sector;
   * with one, they are that sector's names. `sizing` picks the figure mm-be
   * sizes the tiles by at either level, and mm-be's default when absent is
   * market cap.
   *
   * The spec types BOTH levels as `SectorHeatmapTile` — it has no schema for a
   * name — so the rows of a `sector` call are sound here for `key` and `name`
   * only, the two fields the levels share. Anything more goes through
   * `stockHeatmapTileSchema` (schemas/heatmap.ts).
   */
  heatmap: (sector?: string, sizing?: HeatmapSizing): Promise<Page<Wire<'SectorHeatmapTile'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/market/heatmap/', {
        params: {
          query: {
            ...(sector === undefined ? {} : { sector }),
            ...(sizing === undefined ? {} : { sizing }),
          },
        },
      }),
    ),

  indices: (): Promise<Page<Wire<'IndexQuote'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/indices/')),

  indexHistory: (key: string, range: MarketRange): Promise<Wire<'IndexHistory'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/indices/{key}/history/', {
        params: { path: { key }, query: { range } },
      }),
    ),

  /**
   * The latest session a point a minute: `range=1D` (mm-be PR #99), whose `t` is an ISO
   * date-time in Kathmandu where every other range sends a date. Before the open it is the
   * previous session.
   */
  indexIntraday: (key: string): Promise<Wire<'IndexHistory'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/indices/{key}/history/', {
        params: { path: { key }, query: { range: '1D' } },
      }),
    ),

  live: (limit = LIVE_MAX_LIMIT, sector?: string): Promise<Page<Wire<'LiveRow'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/market/live/', {
        params: { query: { limit, ...(sector === undefined ? {} : { sector }) } },
      }),
    ),

  movers: (query: MoversParams): Promise<Page<Wire<'MoverRow'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/movers/', { params: { query } })),

  search: (q: string, limit = 20): Promise<Page<Wire<'SearchResult'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/search/', { params: { query: { q, limit } } })),

  sectors: (sort: SectorSort = 'change'): Promise<Page<Wire<'SectorRow'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/sectors/', { params: { query: { sort } } })),

  sector: (symbol: string): Promise<Wire<'SectorSummary'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/sectors/{symbol}/', {
        params: { path: { symbol } },
      }),
    ),

  sectorTurnover: (): Promise<Page<Wire<'SectorTurnoverSlice'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/sectors/turnover-distribution/')),

  status: (): Promise<Wire<'MarketStatus'>> =>
    getData(() => apiClient.GET('/api/v5/market/status/')),

  /** The latest `days` Fear & Greed readings, one per session, oldest first. */
  fearGreed: (days: number): Promise<Wire<'FearGreedDay'>[]> =>
    getData(() => apiClient.GET('/api/v5/market/fear-greed/', { params: { query: { days } } })),

  priceHistory: (symbol: string, range: MarketRange): Promise<Wire<'PriceSeries'>> =>
    getData(() =>
      apiClient.GET('/api/v5/market/stocks/{symbol}/price-history/', {
        params: { path: { symbol }, query: { range } },
      }),
    ),

  summary: (): Promise<Wire<'MarketSummary'>> =>
    getData(() => apiClient.GET('/api/v5/market/summary/')),

  tradingAverage: (limit = 50): Promise<Page<Wire<'TradingAverageRow'>>> =>
    getPage(() =>
      apiClient.GET('/api/v5/market/trading-average/', { params: { query: { limit } } }),
    ),

  updates: (query: UpdatesParams): Promise<Page<Wire<'MarketUpdate'>>> =>
    getPage(() => apiClient.GET('/api/v5/market/updates/', { params: { query } })),
}
